import { ReactNode } from 'react';
import { IntlShape } from 'react-intl';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { CardProps } from '@designsystem/stories/03-molecules/Card';
import type { OccasionProps } from '@fhnw/types/occasion';

// Flexible interface that can accept any Content-like object
// Using intersection type to avoid inheritance conflicts
export type NormalizableContent = Partial<Content> &
  Partial<OccasionProps> & {
    // Additional fields that might be present
    parent?: Content;
    category?: string | ReactNode;
    subjects?: string[];
    Subject?: string[];
    university_tokens?: { title: string }[];
    organisation_entities?: { title: string }[];

    // Person-specific fields
    name_affix1?: string;
    role?: string | string[];
    phone1?: string;
    phone1_suffix?: string;
    phone2?: string;
    phone2_suffix?: string;
    email?: string;
    booking_url?: string;
    google_maps_url?: string;

    // Event-specific fields
    event_type?: string | string[];
    occasions?: OccasionProps[];

    // Course-specific fields
    degree_program_type?: string | string[];
    bulletpoints?: string[];
    next_start_fallback?: string | string[];
    study_mode?: string | string[];
    duration?: string;
    languages_used?: string | string[];

    // Training-specific fields
    continuing_education_type?: string | string[];
    education_format?: string | string[];
    target_audience?: string | string[];
    cost_absorption?: string | string[];

    // Project-specific fields
    project_type?: string | string[];

    // Product-specific fields
    product_name?: string;

    // Panel-specific fields
    panel_icon?: string | string[];

    // Registration-specific fields
    application_button_link?: string;
    new_tab?: boolean;
    waiting_list?: boolean;
    additional_information?: string;

    // Allow any additional properties for maximum flexibility
    [key: string]: any;
  };

// More specific types for type-safe usage in adapters
export type PersonContent = NormalizableContent & {
  name_affix1?: string;
  role?: string | string[];
  phone1?: string;
  phone1_suffix?: string;
  phone2?: string;
  phone2_suffix?: string;
  email?: string;
  booking_url?: string;
  google_maps_url?: string;
};

export type EventContent = NormalizableContent & {
  event_type?: string | string[];
  occasions?: OccasionProps[];
};

export type NewsContent = NormalizableContent & {
  news_category?: string | string[];
};

export type CourseContent = NormalizableContent & {
  degree_program_type?: string | string[];
  bulletpoints?: string[];
  next_start_fallback?: string | string[];
  study_mode?: string | string[];
  duration?: string;
  languages_used?: string | string[];
  occasions?: OccasionProps[];
};

export type TrainingContent = CourseContent & {
  continuing_education_type?: string | string[];
  education_format?: string | string[];
  target_audience?: string | string[];
  cost_absorption?: string | string[];
};

export type ProductContent = NormalizableContent & {
  product_name?: string;
};

export type PanelContent = NormalizableContent & {
  panel_icon?: string | string[];
};

export type RegisterContent = NormalizableContent & {
  // Occasion-specific fields (avoiding inheritance conflicts)
  occasion_title?: string;
  occasion_start?: string | Date;
  occasion_end?: string | Date;
  rolling_intake?: boolean;
  show_from?: string | Date;
  show_until?: string | Date;
  open_end?: boolean;
  whole_day?: boolean;
  duration?: string;
  school_days?: string | string[] | any[];
  place?: string;
  place_short?: string;
  course_management?: string;
  application_start?: string | Date;
  final_application_date?: string | Date;
  total_places?: number;
  free_places?: number;

  // Registration-specific fields
  parent?: Content;
  application_button_link?: string;
  new_tab?: boolean;
  waiting_list?: boolean;
  additional_information?: string;
};

export type CardType = CardProps['type'];
export type CardStyle = CardProps['style'];

export interface TypeNormalizer {
  (
    item: NormalizableContent,
    normalized: CardProps,
    intl: IntlShape,
    university?: NormalizableContent,
    imageSizes?: Record<string, string>,
    onAnmelden?: (item: RegisterContent) => void,
  ): CardProps;
}

export const CARD_TYPES: Record<string, CardType> = {
  Document: 'product',
  DegreeProgram: 'course',
  Contact: 'person',
  TeamContact: 'person',
  // Solr search results carry the FTI title as @type, not the portal_type
  'Team Contact': 'person',
  InfoEvent: 'event',
  FHNWEvent: 'event',
  FHNWNews: 'news',
  University: 'school',
  ContinuingEducation: 'training',
  ResearchArea: 'researchfield',
  Project: 'project',
  Occasion: 'register',
  Specialization: 'specialization',
  ResearchFocus: 'researchfocus',
  Service: 'service',
  Topic: 'topic',
} as const;

export type ImageSizes = Record<string, string>;
